/* eslint-disable no-param-reassign */
import createBaseSurveyMeta from './_BASE';
import colors from '../../Colors';

import {
	computeUuid,
	computeFormattedStationId,
	mapGpsDistance,
	computeDistance,
} from '../util/readings.parse.computeProps';

import { defaultCreateSimplifiedReadings } from '../util/general';
import { createComputeYMinYMax } from '../util/yMinYMax';
import {
	createReadingKeyProps,
	createGetReadingKeys,
	createGetReadingKeyProps,
	createGetColumns,
	createGetSurveyColor,
	createTransformToCsvJson
} from './util';

const INVERT_Y_AXIS = false;

// ─── Y LAYOUT CONSTANTS ──────────────────────────────────────────────────────
// Two bands on one chart:
//   Y = LAND_USE_Y          → single land use band (all states, plugin-rendered)
//   Y = ALIAS_START_Y …     → ROW alias categories (sparse scatter points)
//
// Adjusting Y_GAP_COUNT changes the vertical gap between the two bands.
// All alias category Y values are computed from ALIAS_START_Y automatically.

const LAND_USE_Y         = 1;
const Y_GAP_COUNT        = 1;
const ALIAS_START_Y      = LAND_USE_Y + Y_GAP_COUNT + 1; // = 3
const YMINMAX            = { yMin: 0, yMax: ALIAS_START_Y + 10 }; // = { 0, 13 }

// ─── LAND USE BAND CONSTANTS ─────────────────────────────────────────────────
const LAND_USE_BAND_HEIGHT    = 21; // px, fixed pixel height of the rendered band

// ─── LEGEND & FALLBACK TILE URLS ─────────────────────────────────────────────
// Swap LAND_USE_LEGEND_ICON_URL to change the legend icon for the Land Use entry.
// Swap FOREST_TILE_FALLBACK_URL when a real ROW_tile_forest.png asset is added
// (also add iconUrl to the Forest entry in LAND_STATE_PROPS below).
const LAND_USE_LEGEND_ICON_URL = '/Content/Images/Surveys/ROW_tile_native_grass.png';
const FOREST_TILE_FALLBACK_URL = '/Content/Images/Surveys/ROW_tile_brush.png';

// ─── LAND USE STATE PROPS ────────────────────────────────────────────────────
// Maps each Falcon LandUseState string to:
//   prop    — legacy boolean prop name (kept for any external consumers)
//   iconUrl — tile PNG used by the plugin to fill the land use band
//
// Forest has no dedicated tile asset; FOREST_TILE_FALLBACK_URL is used until
// ROW_tile_forest.png is provided (no code changes needed — just add the asset
// and update the iconUrl here).
const LAND_STATE_PROPS = {
	Forest:          { prop: 'hasLandStateForest',          iconUrl: FOREST_TILE_FALLBACK_URL },
	Swamp:           { prop: 'hasLandStateSwamp',           iconUrl: '/Content/Images/Surveys/ROW_tile_swamp.png' },
	Marsh:           { prop: 'hasLandStateMarsh',           iconUrl: '/Content/Images/Surveys/ROW_tile_marshland.png' },
	Wetland:         { prop: 'hasLandStateWetland',         iconUrl: '/Content/Images/Surveys/ROW_tile_primarily_submerged.png' },
	Pasture:         { prop: 'hasLandStatePasture',         iconUrl: '/Content/Images/Surveys/ROW_tile_pasture.png' },
	Cropland:        { prop: 'hasLandStateCropland',        iconUrl: '/Content/Images/Surveys/ROW_tile_crops.png' },
	Manicured:       { prop: 'hasLandStateManicured',       iconUrl: '/Content/Images/Surveys/ROW_tile_lawn.png' },
	NativeGrassland: { prop: 'hasLandStateNativeGrassland', iconUrl: '/Content/Images/Surveys/ROW_tile_native_grass.png' },
	Transportation:  { prop: 'hasLandStateTransportation',  iconUrl: '/Content/Images/Surveys/ROW_tile_surfaced.png' },
	Clay:            { prop: 'hasLandStateClay',            iconUrl: '/Content/Images/Surveys/ROW_tile_clay.png' },
	Silt:            { prop: 'hasLandStateSilt',            iconUrl: '/Content/Images/Surveys/ROW_tile_silt.png' },
	Sand:            { prop: 'hasLandStateSand',            iconUrl: '/Content/Images/Surveys/ROW_tile_sandy.png' },
	Gravel:          { prop: 'hasLandStateGravel',          iconUrl: '/Content/Images/Surveys/ROW_tile_gravel.png' },
	Cobble:          { prop: 'hasLandStateCobble',          iconUrl: '/Content/Images/Surveys/ROW_tile_cobble.png' },
	Rocky:           { prop: 'hasLandStateRocky',           iconUrl: '/Content/Images/Surveys/ROW_tile_rocky.png' },
	Brush:           { prop: 'hasLandStateBrush',           iconUrl: '/Content/Images/Surveys/ROW_tile_brush.png' },
};

// ─── IMAGE PRELOAD ────────────────────────────────────────────────────────────
// Fire off all tile image requests at module load time so ctx.createPattern()
// receives a complete image on the first chart draw. The browser HTTP-caches
// these; getOrCreatePointImage() in data.chart.series.js creates its own Image
// elements pointing to the same URLs, which resolve immediately from cache.
const _preloadTiles = () => {
	Object.values(LAND_STATE_PROPS).forEach(({ iconUrl }) => {
		const img = new window.Image();
		img.src = iconUrl;
	});
};
_preloadTiles();

// ─── ROW ALIAS CATEGORIES ────────────────────────────────────────────────────
// r.rowaliasidlist is a number (e.g. 110, 22, 43) — primary source.
// r.rowaliaslist is a string (e.g. 'Ground Entry or Exit') — fallback.
// Both are sparse: only present when a physical feature was recorded.

// Human-readable display label for each alias category — used in the readings table.
const ALIAS_CATEGORY_LABELS = {
	hasSign:              'Sign',
	hasFence:             'Fence',
	hasRoad:              'Road',
	hasFlowingWater:      'Flowing Water',
	hasStandingWater:     'Standing Water',
	hasPipeCableCrossing: 'Pipe/Cable Crossing',
	hasGroundEntryExit:   'Ground Entry/Exit',
	hasHillBerm:          'Hill/Berm',
	hasHoleDitch:         'Hole/Ditch',
	hasStructure:         'Structure',
};

// All y values computed from ALIAS_START_Y — adjust Y_GAP_COUNT above to
// shift all alias categories up or down without touching this object.
const ALIAS_CATEGORIES = {
	hasSign:              { y: ALIAS_START_Y,     codeMin: 1,   codeMax: 10  },
	hasFence:             { y: ALIAS_START_Y + 1, codeMin: 20,  codeMax: 29  },
	hasRoad:              { y: ALIAS_START_Y + 2, codeMin: 40,  codeMax: 49  },
	hasFlowingWater:      { y: ALIAS_START_Y + 3, codeMin: 60,  codeMax: 65  },
	hasStandingWater:     { y: ALIAS_START_Y + 4, codeMin: 80,  codeMax: 85  },
	hasPipeCableCrossing: { y: ALIAS_START_Y + 5, codeMin: 100, codeMax: 104 },
	hasGroundEntryExit:   { y: ALIAS_START_Y + 6, codeMin: 110, codeMax: 112 },
	hasHillBerm:          { y: ALIAS_START_Y + 7, codeMin: 120, codeMax: 124 },
	hasHoleDitch:         { y: ALIAS_START_Y + 8, codeMin: 130, codeMax: 135 },
	hasStructure:         { y: ALIAS_START_Y + 9, codeMin: 150, codeMax: 156 },
};

// Text-label fallback — maps each alias string to its category prop.
// Source: SurveyType.ROW.md — ROW Alias Catalog.
const ALIAS_LABEL_TO_CATEGORY = {
	// Signs
	'Sign':                       'hasSign',
	'Pipeline Marker':             'hasSign',
	'Aerial Marker':               'hasSign',
	'Foreign Line Marker':         'hasSign',
	'Abandoned Pipeline Marker':   'hasSign',
	'GPS Control Marker':          'hasSign',
	'Milepost':                    'hasSign',
	'Traffic Sign':                'hasSign',
	'ROW Boundary Post':           'hasSign',
	'Test Post':                   'hasSign',
	// Fences
	'Fence':                       'hasFence',
	'Barbed Wire Fence':           'hasFence',
	'Chain Link Fence':            'hasFence',
	'Wood Fence':                  'hasFence',
	'Pipe Fence':                  'hasFence',
	'Masonry Fence':               'hasFence',
	'Electric Fence':              'hasFence',
	'Game Fence':                  'hasFence',
	'Snow Fence':                  'hasFence',
	'Fence Post':                  'hasFence',
	// Roads
	'Road':                        'hasRoad',
	'Pedestrian Path':             'hasRoad',
	'Driveway':                    'hasRoad',
	'2-Track or Trail':            'hasRoad',
	'Gravel Road':                 'hasRoad',
	'Paved Road':                  'hasRoad',
	'Primary Highway':             'hasRoad',
	'Secondary Highway':           'hasRoad',
	'Railroad Tracks':             'hasRoad',
	'Bridge':                      'hasRoad',
	// Flowing Water
	'Flowing Water':               'hasFlowingWater',
	'Run-Off':                     'hasFlowingWater',
	'Creek':                       'hasFlowingWater',
	'River':                       'hasFlowingWater',
	'Channel':                     'hasFlowingWater',
	'Irrigation Ditch':            'hasFlowingWater',
	// Standing Water
	'Standing Water':              'hasStandingWater',
	'Pond':                        'hasStandingWater',
	'Lake':                        'hasStandingWater',
	'Swamp or Slough':             'hasStandingWater',
	// Pipe or Cable Crossings
	'Pipe or Cable Crossing':      'hasPipeCableCrossing',
	'Overhead Cables':             'hasPipeCableCrossing',
	'Underground Cables':          'hasPipeCableCrossing',
	'Above Ground Pipe':           'hasPipeCableCrossing',
	'Below Ground Pipe':           'hasPipeCableCrossing',
	// Ground Entry or Exit
	'Ground Entry or Exit':        'hasGroundEntryExit',
	'Riser':                       'hasGroundEntryExit',
	'Span':                        'hasGroundEntryExit',
	// Hills and Berms
	'Hill':                        'hasHillBerm',
	'Base of Hill':                'hasHillBerm',
	'Crest of Hill':               'hasHillBerm',
	'Base of Berm':                'hasHillBerm',
	'Crest of Berm':               'hasHillBerm',
	// Holes and Ditches
	'Hole':                        'hasHoleDitch',
	'Sinkhole or Settled Ground':  'hasHoleDitch',
	'Construction Excavation':     'hasHoleDitch',
	'Ditch':                       'hasHoleDitch',
	'Roadside Ditch':              'hasHoleDitch',
	'Drainage Ditch':              'hasHoleDitch',
	// Structures
	'Structure':                   'hasStructure',
	'Pipeline Control Building':   'hasStructure',
	'Foreign Building':            'hasStructure',
	'Metering Building':           'hasStructure',
	'Power Pole':                  'hasStructure',
	'Anchor':                      'hasStructure',
	'Vault':                       'hasStructure',
};

const isDefined = v => v !== undefined && v !== null;

// eslint-disable-next-line no-unused-vars
export default _survey => {
	// CONSTANTS

	const gapThreshold = 200;

	// READING KEYS
	// Band 1 (Y = LAND_USE_Y): single land use placeholder dataset.
	//   No readings are ever assigned to this dataset (value: 'landUse' is never
	//   set on any reading). Its sole purpose is to drive the Land Use legend item
	//   and the Redux visibility toggle. The actual band rendering is done by the
	//   rowLandUse Chart.js plugin, which reads spans from chart.options.plugins.rowLandUse.
	//
	// Band 2 (Y = ALIAS_START_Y … ALIAS_START_Y+9): ROW alias categories — sparse scatter points.

	const readingKeys = [
		// ── Land Use (single placeholder dataset) ────────────────────────────
		{
			label: 'Land Use',
			value: 'landUse',       // never set on any reading — data: [] always
			seriesType: 'Scatter',
			iconUrl: LAND_USE_LEGEND_ICON_URL,
			color: colors.getColor(),
			showInTooltip: false,
			yAxisUnitDisplayText: 'ROW & Land Use'
		},
		// ── ROW Alias Categories ─────────────────────────────────────────────
		{
			label: 'Signs',
			value: 'hasSign',
			seriesType: 'Scatter',
			marker: 'triangle',
			iconUrl: '/Content/Images/Surveys/ROW_icon_sign.png',
			colorize: true,
			color: colors.getColor(),
			showInTooltip: true,
			tooltipValueKey: 'rowaliaslist',
			yAxisUnitDisplayText: 'ROW & Land Use'
		},
		{
			label: 'Fences',
			value: 'hasFence',
			seriesType: 'Scatter',
			marker: 'rect',
			iconUrl: '/Content/Images/Surveys/ROW_icon_fence.png',
			colorize: true,
			color: colors.getColor(),
			showInTooltip: true,
			tooltipValueKey: 'rowaliaslist',
			yAxisUnitDisplayText: 'ROW & Land Use'
		},
		{
			label: 'Roads',
			value: 'hasRoad',
			seriesType: 'Scatter',
			marker: 'circle',
			iconUrl: '/Content/Images/Surveys/ROW_icon_road.png',
			colorize: true,
			color: colors.getColor(),
			showInTooltip: true,
			tooltipValueKey: 'rowaliaslist',
			yAxisUnitDisplayText: 'ROW & Land Use'
		},
		{
			label: 'Flowing Water',
			value: 'hasFlowingWater',
			seriesType: 'Scatter',
			marker: 'rectRot',
			iconUrl: '/Content/Images/Surveys/ROW_icon_flowing_water.png',
			colorize: true,
			color: colors.getColor(),
			showInTooltip: true,
			tooltipValueKey: 'rowaliaslist',
			yAxisUnitDisplayText: 'ROW & Land Use'
		},
		{
			label: 'Standing Water',
			value: 'hasStandingWater',
			seriesType: 'Scatter',
			marker: 'rectRounded',
			iconUrl: '/Content/Images/Surveys/ROW_icon_standing_water.png',
			colorize: true,
			color: colors.getColor(),
			showInTooltip: true,
			tooltipValueKey: 'rowaliaslist',
			yAxisUnitDisplayText: 'ROW & Land Use'
		},
		{
			label: 'Pipe/Cable Crossings',
			value: 'hasPipeCableCrossing',
			seriesType: 'Scatter',
			marker: 'star',
			iconUrl: '/Content/Images/Surveys/ROW_icon_pipe_cable.png',
			colorize: true,
			color: colors.getColor(),
			showInTooltip: true,
			tooltipValueKey: 'rowaliaslist',
			yAxisUnitDisplayText: 'ROW & Land Use'
		},
		{
			label: 'Ground Entry or Exit',
			value: 'hasGroundEntryExit',
			seriesType: 'Scatter',
			marker: 'cross',
			iconUrl: '/Content/Images/Surveys/ROW_icon_entry_exit.png',
			colorize: true,
			color: colors.getColor(),
			showInTooltip: true,
			tooltipValueKey: 'rowaliaslist',
			yAxisUnitDisplayText: 'ROW & Land Use'
		},
		{
			label: 'Hills and Berms',
			value: 'hasHillBerm',
			seriesType: 'Scatter',
			marker: 'crossRot',
			iconUrl: '/Content/Images/Surveys/ROW_icon_hill.png',
			colorize: true,
			color: colors.getColor(),
			showInTooltip: true,
			tooltipValueKey: 'rowaliaslist',
			yAxisUnitDisplayText: 'ROW & Land Use'
		},
		{
			label: 'Holes and Ditches',
			value: 'hasHoleDitch',
			seriesType: 'Scatter',
			marker: 'dash',
			iconUrl: '/Content/Images/Surveys/ROW_icon_hole.png',
			colorize: true,
			color: colors.getColor(),
			showInTooltip: true,
			tooltipValueKey: 'rowaliaslist',
			yAxisUnitDisplayText: 'ROW & Land Use'
		},
		{
			label: 'Structures',
			value: 'hasStructure',
			seriesType: 'Scatter',
			marker: 'line',
			iconUrl: '/Content/Images/Surveys/ROW_icon_structure.png',
			colorize: true,
			color: colors.getColor(),
			showInTooltip: true,
			tooltipValueKey: 'rowaliaslist',
			yAxisUnitDisplayText: 'ROW & Land Use'
		},
	];

	const readingKeyProps = createReadingKeyProps(readingKeys);
	const _computeYMinYMax = createComputeYMinYMax(readingKeys);
	const computeYMinYMax = (survey, currReading) => {
		_computeYMinYMax(survey, currReading);
		survey.yMin = YMINMAX.yMin;
		survey.yMax = YMINMAX.yMax;
	};

	// SKIP READING
	// landusestate is present on every reading, so no reading should ever be
	// skipped — every GPS point contributes to the land use band.

	// eslint-disable-next-line no-unused-vars
	const isSkipReading = _reading => false;

	// HAS GAP

	const hasGap = (currReading, prevReading) => {
		return !!prevReading && currReading.computedDistance - prevReading.id > gapThreshold;
	};

	// INTERPOLATE

	const interpolate = (
		// eslint-disable-next-line no-unused-vars
		_prevReading,
		// eslint-disable-next-line no-unused-vars
		_nextReading,
		// eslint-disable-next-line no-unused-vars
		_alignToReading
	) => {};

	// COMPUTE PROPS - to be used by readings.parse

	const computeHilo = r => {
		r.low = r.low || 0;
	};

	// Sets the alias category prop when the reading has a ROW feature.
	// Primary: r.rowaliasidlist (number) — checked against code ranges.
	// Fallback: r.rowaliaslist (string) — looked up in ALIAS_LABEL_TO_CATEGORY.
	const computeRowFeatures = r => {
		const code = isDefined(r.rowaliasidlist) ? parseInt(r.rowaliasidlist, 10) : NaN;

		if (!Number.isNaN(code)) {
			const match = Object.entries(ALIAS_CATEGORIES).find(
				([, { codeMin, codeMax }]) => code >= codeMin && code <= codeMax
			);
			if (match) {
				const [prop, { y }] = match;
				r[prop] = y;
				r.rowaliasCategory = ALIAS_CATEGORY_LABELS[prop];
			}
			return;
		}

		// Fallback to text label
		const label = r.rowaliaslist;
		if (!isDefined(label) || label.toString().trim() === '') {
			return;
		}

		const prop = ALIAS_LABEL_TO_CATEGORY[label.toString().trim()];
		if (prop) {
			r[prop] = ALIAS_CATEGORIES[prop].y;
			r.rowaliasCategory = ALIAS_CATEGORY_LABELS[prop];
		}
	};

	const computeProps = (survey, _prevReading, currReading, currIdx) => {
		computeUuid(currReading, currIdx);
		computeFormattedStationId(currReading);
		computeHilo(currReading);
		computeYMinYMax(survey, currReading);
		mapGpsDistance(currReading);
		computeDistance(_prevReading, currReading);
		computeRowFeatures(currReading);
	};

	// SIMPLIFIED READINGS - creates a smaller data set to be used for rendering polyline on map
	const createSimplifiedReadings = defaultCreateSimplifiedReadings;

	// ── COMPUTE LAND USE SPANS ───────────────────────────────────────────────
	// Walks the readings array and returns an array of span tuples describing
	// consecutive land use state segments. Called from DataChart.js and the
	// result is stored in chart.options.plugins.rowLandUse.spans for the plugin.
	//
	// Tuple shape: { state, iconUrl, xStart, xEnd }
	//   xStart — gpsdistance of the first reading in the span
	//   xEnd   — gpsdistance of the last reading in the span
	//
	// Gaps (distance > gapThreshold) cause the current span to close and a new
	// one to open after the gap, preserving physical discontinuities.
	const computeLandUseSpans = readings => {
		const spans = [];
		let currentState   = null;
		let currentIconUrl = null;
		let xStart         = null;
		let prevReading    = null;

		(readings || []).forEach(reading => {
			const state   = reading.landusestate;
			const gpsDist = reading.gpsdistance;

			const gapDetected =
				prevReading !== null &&
				gpsDist - prevReading.gpsdistance > gapThreshold;

			// Step 1: close span on gap
			if (gapDetected && currentState !== null) {
				spans.push({ state: currentState, iconUrl: currentIconUrl, xStart, xEnd: prevReading.gpsdistance });
				currentState   = null;
				xStart         = null;
			}

			// Step 2: close span on state change, open new span
			if (state !== currentState) {
				if (currentState !== null) {
					spans.push({ state: currentState, iconUrl: currentIconUrl, xStart, xEnd: prevReading.gpsdistance });
				}
				currentState   = state;
				currentIconUrl = (LAND_STATE_PROPS[state] && LAND_STATE_PROPS[state].iconUrl) || FOREST_TILE_FALLBACK_URL;
				xStart         = gpsDist;
			}

			prevReading = reading;
		});

		// Step 3: close the final open span
		if (currentState !== null && prevReading !== null) {
			spans.push({ state: currentState, iconUrl: currentIconUrl, xStart, xEnd: prevReading.gpsdistance });
		}

		return spans;
	};

	const columns = [
		{ label: 'ID', value: 'id', checked: true, width: 48, required: true },
		{
			label: 'Station',
			value: 'formattedDst',
			checked: true,
			width: 80,
			showInTooltip: false
		},
		{ label: 'ROW', value: 'rowaliaslist', checked: true, width: 160 },
		{ label: 'LAND', value: 'landusestate', checked: true, width: 130 },
		{ label: 'TERRAIN', value: 'terrain', checked: true, width: 80 },
		{
			label: 'LAT',
			value: 'lat',
			checked: true,
			width: 65,
			getCsvValue: r => {
				if (isDefined(r.lat)) {
					return r.lat;
				}
				if (isDefined(r.loc)) {
					return r.loc[0];
				}
				return undefined;
			}
		},
		{
			label: 'LNG',
			value: 'lng',
			checked: true,
			width: 70,
			getCsvValue: r => {
				if (isDefined(r.lng)) {
					return r.lng;
				}
				if (isDefined(r.loc)) {
					return r.loc[1];
				}
				return undefined;
			}
		},
		{
			label: 'COMMENTS',
			value: 'cmt',
			checked: true,
			width: 200,
			type: 'text'
		}
	];

	// MAIN
	const getReadingKeys = createGetReadingKeys(readingKeys);
	const getColumns = createGetColumns(columns);
	const transformToCsvJson = createTransformToCsvJson(getColumns);

	const meta = {
		...createBaseSurveyMeta(_survey),
		isChartInversed: INVERT_Y_AXIS,
		getReadingKeys,
		getReadingKeyProps: createGetReadingKeyProps(readingKeyProps),
		getColumns,
		transformToCsvJson,
		getSurveyColor: createGetSurveyColor(getReadingKeys),
		fkey: 'none',
		isSkipReading,
		hasGap,
		interpolate,
		computeProps,
		computeYMinYMax,
		createSimplifiedReadings,
		// ── Land use plugin interface ─────────────────────────────────────────
		// computeLandUseSpans: called by DataChart.js after chart init; result
		//   stored in chart.options.plugins.rowLandUse.spans for the plugin.
		// landUsePluginConfig: spread into chart.options.plugins.rowLandUse so
		//   the plugin receives y and bandHeight without importing from this file.
		hideYAxisTicks: true,
		computeLandUseSpans,
		landUsePluginConfig: {
			y:          LAND_USE_Y,
			bandHeight: LAND_USE_BAND_HEIGHT,
		},
	};

	return meta;
};
